import React, {Component} from 'react';
import {inject, observer} from 'mobx-react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import first from 'lodash/first';

//enums
import {DOMAIN_LOGIN_OPTIONS} from 'enums';

//components
import {ButtonGroup} from 'components/Button';
import {FormattedMessage} from 'components/FormattedComponents';
import Icon from 'components/Icon';

//messages
import messages from './messages';

//styles
import {PickerContainer, StyledSignInOptionButtonDesktop, StyledSignInOptionButtonMobile, StyledButton} from './styles';

import {posthogCapture} from '../../utils/posthog-utils';
import {posthogEvents} from '../../posthog-events';
import EmailAndPasswordLogin from '../EmailAndPasswordLogin';
import kebabCase from 'lodash/kebabCase';

@injectIntl
@inject('store')
@observer
class SignInOptionsPickerComponent extends Component {
  static propTypes = {
    device: PropTypes.oneOf(['desktop', 'mobile']).isRequired,
    views: PropTypes.object.isRequired,

    /**
     * The url to redirect to after signing in
     */
    redirect: PropTypes.string,

    /**
     * If true, the redirect will be passed as a query param when navigating to the sign in page
     */
    preserveRedirect: PropTypes.bool
  };

  componentDidMount() {
    const {store} = this.props;
    store.platform.fetchPlatformFlags();
  }

  buildSsoUrl(href, id) {
    const ssoUrl = new URL(`${window.location.origin}/sso/login`);
    ssoUrl.searchParams.append('href', href);
    ssoUrl.searchParams.append('origin', 'viewer');
    id && ssoUrl.searchParams.append('ssoProviderInstance', id);
    return ssoUrl;
  }

  getIconName(name) {
    if (name === 'azureadoauth2' || name === 'googleoauth2') {
      return name;
    }
    return 'briefcase';
  }

  render() {
    const {intl, store, device, views, redirect, preserveRedirect} = this.props;
    const {formatMessage} = intl;
    const {platform} = store;
    const {
      domain: {loginOptions},
      isContentProtected,
      developmentFeatureFlags: {contactMethodSelection},
      ssoProviders
    } = platform;

    const style = {letterSpacing: '0', fontWeight: '400'};

    const redirectParams = preserveRedirect ? {queryParams: {redirect}} : {};

    const SignInOptionsConfig = filter(
      [
        ...(ssoProviders || []).map(provider => {
          const ssoUrl = this.buildSsoUrl(redirect, provider.id);
          return {
            id: DOMAIN_LOGIN_OPTIONS.SSO,
            label: provider.label
              ? formatMessage(messages.loginWithProvider, {provider: provider.label})
              : formatMessage(messages.ssoLogin),
            shouldShow: Boolean(provider.provider),
            iconName: this.getIconName(provider.provider),
            posthogEvent: posthogEvents.selectSsoSignIn,
            urlProps: {
              href: ssoUrl.href
            }
          };
        }),
        {
          id: DOMAIN_LOGIN_OPTIONS.EMAIL,
          label: formatMessage(messages.emailAndPasswordLogin),
          shouldShow: includes(loginOptions, DOMAIN_LOGIN_OPTIONS.EMAIL),
          iconName: 'email-outline',
          posthogEvent: posthogEvents.selectEmailSignIn,
          urlProps: {
            store,
            route: views.signIn,
            params: {
              loginType: DOMAIN_LOGIN_OPTIONS.EMAIL.toLowerCase()
            }
          }
        },
        {
          id: DOMAIN_LOGIN_OPTIONS.PHONE_NUMBER,
          label: formatMessage(messages.phoneNumber),
          shouldShow: includes(loginOptions, DOMAIN_LOGIN_OPTIONS.EMAIL) && contactMethodSelection,
          iconName: 'phone',
          urlProps: {
            store,
            route: views.signIn,
            params: {
              loginType: kebabCase(DOMAIN_LOGIN_OPTIONS.PHONE_NUMBER.toLowerCase())
            }
          }
        },
        {
          id: DOMAIN_LOGIN_OPTIONS.DOMAIN_PASSWORD,
          shouldShow: includes(loginOptions, DOMAIN_LOGIN_OPTIONS.DOMAIN_PASSWORD),
          label: formatMessage(messages.domainPasswordLogin),
          iconName: 'workspace',
          posthogEvent: posthogEvents.selectWorkspacePasswordSignIn,
          urlProps: {
            store,
            route: views.signIn,
            params: {loginType: DOMAIN_LOGIN_OPTIONS.DOMAIN_PASSWORD.toLowerCase()}
          }
        }
      ],
      'shouldShow'
    );
    const StyledSignInOptionButton =
      device === 'desktop' ? StyledSignInOptionButtonDesktop : StyledSignInOptionButtonMobile;

    const shouldRenderOptionDirectly =
      SignInOptionsConfig.length === 1 && first(SignInOptionsConfig).id !== DOMAIN_LOGIN_OPTIONS.SSO;

    const CurrentComponent = shouldRenderOptionDirectly ? (
      <EmailAndPasswordLogin loginOption={first(SignInOptionsConfig).id} />
    ) : (
      <React.Fragment>
        {SignInOptionsConfig.map(signInOption => (
          <StyledSignInOptionButton
            key={signInOption.id}
            data-testid={`sign-in-option-${signInOption.id}`}
            iconName={signInOption.iconName}
            iconComponent={Icon}
            onClick={posthogCapture(signInOption.posthogEvent)}
            {...redirectParams}
            {...signInOption.urlProps}
          >
            {signInOption.label}
          </StyledSignInOptionButton>
        ))}
      </React.Fragment>
    );

    return isContentProtected ? (
      <PickerContainer data-testid="picker-container">
        {CurrentComponent}
        <ButtonGroup direction="column">
          {contactMethodSelection && (
            <StyledButton style={style} data-testid="activation-code" href={'/activation-code'}>
              <FormattedMessage {...messages.formAction_iHaveAnActivationCode} />
            </StyledButton>
          )}
          <StyledButton
            style={style}
            data-testid="admin-account"
            href={`/admin/login?redirect=${window.location.href}`}
          >
            <FormattedMessage {...messages.formAction_iHaveAnAdminAccount} />
          </StyledButton>
        </ButtonGroup>
      </PickerContainer>
    ) : null;
  }
}

/**
 * A wrapper around the SignInOptionsPickerComponent that is aware of how it
 * should handle redirections depending on the current route.
 */
const RouteAwareSignInOptionsPicker = inject('store')(
  observer(({store, views, ...props}) => {
    const {
      router: {currentRoute, queryParams}
    } = store;
    const currentLocation = window.location.href;
    const redirectQueryParam = queryParams?.redirect;

    const isOnDomainLoginView = currentRoute === views.domainLogin;
    const preserveRedirect = !isOnDomainLoginView || redirectQueryParam;
    const fallbackRedirect = !isOnDomainLoginView ? currentLocation : `${window.location.origin}${views.explorer.path}`;
    const redirect = queryParams?.redirect ?? fallbackRedirect;

    return (
      <SignInOptionsPickerComponent preserveRedirect={preserveRedirect} redirect={redirect} views={views} {...props} />
    );
  })
);

export default RouteAwareSignInOptionsPicker;
